<template>
  <div class="view valet-details" v-if="verified">
    <h6>{{ $t('valet.details.breadcrumb') }}</h6>
    <div class="details">
      <div>
        <div class="group">
          <Input type="text" :placeholder="$t('valet.details.title.placeholder')" :value="detailsTitle" no-outline read-only no-tracking/>
        </div>
        <div class="group half">
          <Input type="text" :placeholder="$t('registration.form.firstname')" :value="details.firstname" no-outline read-only no-tracking/>
          <Input type="text" :placeholder="$t('registration.form.lastname')" :value="details.lastname" no-outline read-only no-tracking/>
        </div>
        <div class="group half">
          <Input type="text" :placeholder="$t('registration.form.street')" :value="details.street" no-outline read-only no-tracking/>
          <Input type="text" :placeholder="$t('registration.form.streetnr')" :value="details.streetnr" no-outline read-only no-tracking/>
        </div>
        <div class="group half">
          <Input type="text" :placeholder="$t('registration.form.zip')" :value="details.zip" no-outline read-only no-tracking/>
          <Input type="text" :placeholder="$t('registration.form.city')" :value="details.city" no-outline read-only no-tracking/>
        </div>
      </div>

      <div>
        <div class="group">
          <Input type="text" :placeholder="$t('registration.form.email')" :value="details.email" no-outline read-only no-tracking/>
        </div>
        <div class="group half">
          <Input type="text" :placeholder="$t('registration.form.mobile')" :value="details.mobile" no-outline read-only no-tracking/>
          <Input type="text" :placeholder="$t('registration.form.plate')" :value="details.plate" no-outline read-only no-tracking/>
        </div>
        <div class="group">
          <Input type="text" :placeholder="$t('valet.list.confirmed')" :value="detailsOptIn" no-outline read-only no-tracking/>
        </div>
        <div class="group half">
          <Input v-if="model" type="text" :placeholder="$t('registration.form.model.placeholder')" :value="model" no-outline read-only no-tracking/>
          <Input v-if="checkin" type="text" :placeholder="$t('valet.details.checkin')" :value="checkin" no-outline read-only no-tracking/>
        </div>
      </div>
    </div>
    <div class="actions">
      <Button use-arrow red @click="$refs.modal.show()">{{ $t('valet.details.delete') }}</Button>
      <Button use-arrow @click="handleActionClick('edit')">{{ $t('valet.details.edit') }}</Button>
    </div>
    <TextArea :placeholder="$t('valet.details.comment')" :value.sync="comment" no-tracking/>
    <div class="actions">
      <Button gray use-arrow @click="handleActionClick('cancel')">{{ $t('valet.details.cancel') }}</Button>
      <Button use-arrow @click="handleActionClick('save')">{{ $t('valet.details.save') }}</Button>
    </div>
    <template v-if="computedCheckins.length > 0">
      <h6>{{ $t('valet.checkins.breadcrumb') }}</h6>
      <div class="table">
        <table>
          <thead>
          <th>{{ $t('valet.checkins.checkin') }}</th>
          <th>{{ $t('valet.checkins.checkout') }}</th>
          <th>{{ $t('valet.checkins.duration') }}</th>
          </thead>
          <tbody>
          <tr v-for="(row, index) in computedCheckins" :key="index">
            <td>{{ row.checkin }}</td>
            <td>{{ row.checkout }}</td>
            <td>{{ row.duration }}</td>
          </tr>
          </tbody>
        </table>
      </div>
    </template>
    <Modal
      ref="modal"
      :title="$t('valet.details.modal.title')"
      :actions="[
        { label: $t('valet.details.cancel'), value: 'cancel'},
        { label: $t('valet.details.delete'), value: 'delete' },
      ]"
      @action="handleModalAction">
      <p v-html="$t('valet.details.modal.description')"></p>
    </Modal>
  </div>
</template>

<script>
  import moment from 'moment';
  import IconSearch from '@/assets/svg/icon-search.svg?inline';
  import Button from '@/components/ui/Button';
  import Table from '@/components/Table';
  import Input from '@/components/Input';
  import TextArea from '@/components/TextArea';
  import Modal from '@/components/ui/Modal';

  export default {
    name: 'ValetDetails',
    components: {
      Modal,
      TextArea,
      Input,
      Table,
      Button,
      IconSearch,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      uid: {
        type: String,
        default: null,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        verified: false,
        details: '',
        checkins: [],
        comment: null,
      };
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      details(value) {
        this.comment = value.comment || '';
      },
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      computedCheckins() {
        const out = [];
        this.checkins.forEach((row) => {
          out.push({
            checkin: (row.checkin) ? moment.unix(row.checkin).format('DD.MM.YYYY HH:mm') : '-',
            checkout: (row.checkout) ? moment.unix(row.checkout).format('DD.MM.YYYY HH:mm') : '-',
            duration: (row.checkout) ? this.getDuration(row.checkin, row.checkout) : '-',
          });
        });
        return out;
      },
      detailsTitle() {
        if (this.details.title) {
          return this.$t(`valet.details.title.${this.details.title}`) || null;
        }
        return null;
      },
      checkin() {
        const checkin = (this.checkins[0] || {}).checkin || null;
        const checkout = (this.checkins[0] || {}).checkout || null;
        if (checkin !== null && checkout === null) {
          return moment.unix(checkin).format('HH:mm');
        }
        return null;
      },
      detailsOptIn() {
        if (this.details.confirmed !== undefined) {
          return this.$t(`valet.details.confirmed.${this.details.confirmed}`) || false;
        }
        return null;
      },
      model() {
        if (this.details.model && this.details.model !== 'null') {
          return this.details.model;
        }
        return null;
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    filters: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    async created() {
      if (!this.$b.data.verified) {
        this.$router.push(`/${this.$i18n.locale}/valet/login`);
      }
      this.verified = this.$b.data.verified;

      const response = await this.$b.api.get(`registrations/details?uid=${this.uid}`);
      if (response.details) {
        this.details = response.details;
        this.checkins = response.checkins;
      }
    },
    // beforeMount() {},
    // render(h) { return h(); },
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      getDuration(checkin, checkout) {
        const inMoment = moment.unix(checkin);
        const outMoment = moment.unix(checkout);
        const duration = moment.duration(Math.abs(inMoment.diff(outMoment)));
        const days = String(duration.days()).padStart(2, '0');
        const hours = String(duration.hours()).padStart(2, '0');
        const minutes = String(duration.minutes()).padStart(2, '0');
        const seconds = String(duration.seconds()).padStart(2, '0');
        return `${days}:${hours}:${minutes}:${seconds}`;
      },
      //----------------------------------
      // Event Handler
      //----------------------------------
      async handleActionClick(action) {
        if (action === 'edit') {
          this.$router.push(`/${this.$i18n.locale}/valet/edit/${this.uid}`);
        } else {
          if (action === 'save') {
            await this.$b.api.post('registrations/update', { uid: this.uid }, {
              comment: this.comment,
            });
          }
          this.$router.push(`/${this.$i18n.locale}/valet/overview`);
        }
      },
      async handleModalAction(value) {
        if (value === 'delete') {
          await this.$b.api.post('registrations/update', { uid: this.uid }, {
            archived: 1,
          });
          this.$router.push(`/${this.$i18n.locale}/valet/overview`);
        }
        this.$refs.modal.close();
      },
    },
  };
</script>

<style lang="scss">
  @import '~scss/variables';
  @import '~scss/mixins';

  .view.valet-details {
    padding: 25px 20px 0 20px;

    h1 {
      position: relative;
      @include font-daimler-cac();
      font-size: 3.4rem;
      margin: 0 0 55px;
      padding: 0;
    }
    h3 {
      @include font-daimler-cac();
      font-size: 2.8rem;
      margin: 45px 0 0 0;
      padding: 0;
    }
    h6 {
      position: relative;
      @include font-daimler-cs(400);
      font-size: 1.6rem;
      margin: 0;
      padding: 0;

      &:after {
        content: '';
        position: relative;
        display: block;
        height: 2px;
        width: 46px;
        background-color: $color-white;
        margin-top: 25px;
        margin-bottom: 30px;
      }
    }

    a {
      color: $color-gray;
      cursor: pointer;

      span.link {
        display: inline-block;
        &:before {
          display: inline-block;
          position: relative;
          content: '>';
          width: auto;
          height: inherit;
          margin-right: 8px;
          transform-origin: 50% 50%;
          transform: translate(0,1px) rotate(0) scale(1, 1.6);
        }
      }
    }

    .details {
      display: flex;
      & > div {
        width: 50%;
        .group {
          &.half {
            display: flex;

            & > div {
              width: 50%;
            }
          }
        }
      }
    }
    .actions {
      margin-top: 32px;
      display: flex;
      justify-content: flex-end;
      padding-bottom: 32px;

      & > button {
        margin-right: 32px;
        &:last-of-type {
          margin-right: 0;
        }
      }
    }
  }
</style>
